import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { EMPTY, Observable, throwError, of } from 'rxjs';
import {
  tap,
  catchError,
  retryWhen,
  delay,
  mergeMap,
  concat,
} from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { SsConstant } from '@ss-core/constants/ss.constant';
import { SUPPRESS_TOAST_HEADER } from '@ss-core/services';
import { ssToasterErrorConfig } from '@blocks/toastr/ss-toastr.config';

/**
 * HttpClientのrequest/responseをインターセプトする
 * - 成功/失敗時にtoast表示
 *
 * https://angular.io/api/common/http/HttpInterceptor
 */
@Injectable()
export class ToastInterceptor implements HttpInterceptor {
  private base_url: string;
  private isRedirectingToLogin = false;

  constructor(private toastrService: ToastrService, private router: Router) {
    this.base_url = SsConstant.API_BASE_URL;
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    // 呼び出し元が成功トーストの抑制を希望しているか（ヘッダーはAPIに送らず、ここで剥がす）
    const suppressToast = request.headers.has(SUPPRESS_TOAST_HEADER);

    // URLを置換
    const req = request.clone({
      url: this.parseUrl(request.url),
      headers: request.headers.delete(SUPPRESS_TOAST_HEADER),
    });

    // 成功/失敗
    return next.handle(req).pipe(
      retryWithDelay(),
      tap({
        next: (res: HttpResponse<any>) => {
          this.handleResponseSuccess(res, suppressToast);
        },
      }),
      catchError((error: HttpErrorResponse) => {
        return this.handleResponseCatch(error);
      })
    );
  }

  /**
   * 成功時の処理
   * @param suppressToast trueの場合、成功時のトースト表示のみ抑制する（エラートーストは抑制しない）
   */
  private handleResponseSuccess(
    response,
    suppressToast = false
  ): Observable<any> {
    if (!response.body) return response;

    // messageが返されたら表示する
    const { message, result } = response.body;
    if (message) {
      if (result) {
        if (!suppressToast) {
          this.toastrService.success(message);
        }
      } else {
        this.toastrService.error(message, '', ssToasterErrorConfig);
      }
    }

    return response;
  }

  /**
   * 失敗時の処理
   */
  private handleResponseCatch(response): Observable<any> {
    console.error('handleResponseCatch', this, response);

    // messageが返されたら表示する
    const message = response.error?.message || response.message;
    if (message && response.status !== 401) {
      // (message, title, ToastConfig)
      // https://www.npmjs.com/package/ngx-toastr
      this.toastrService.error(message, '', ssToasterErrorConfig);
    }

    // statusCodeでの分岐処理
    switch (response.status) {
      case 401:
        // Treat an expired or revoked session as logout. Completing the
        // request prevents an unhandled HTTP error from opening Sentry's
        // crash report dialog while the login page is being displayed.
        if (!this.isRedirectingToLogin && this.router.url !== '/login') {
          this.isRedirectingToLogin = true;
          this.router.navigate(['/login'], { replaceUrl: true }).then(
            () => (this.isRedirectingToLogin = false),
            () => (this.isRedirectingToLogin = false)
          );
        }
        return EMPTY;
      case 404:
        // 404ページに飛ばす（/404 -> path: '**'）
        this.router.navigate(['/404']);
      case 500:
      case 502:
      case 0:
      default:
        // 例外で落とす（Sentryダイアログ起動）
        throw new Error(response);
    }

    // catchErrorはObservableを返すか、throwで終わる必要がある
    return throwError(response);
  }

  /**
   * URLを変換（baseUrlを追加）
   * From: /auth/login
   * To: /api/auth/login
   */
  parseUrl(url: string) {
    // 外部のURLを叩いてる場合はそのまま返す
    if (/^https?:\/\//.test(url)) {
      return url;
    }
    return this.base_url + url;
  }
}

/**
 * errorが起きたら3秒後にリトライ
 * - ３回まで
 * - 404, 401は除外
 *
 * https://rxjs.dev/api/operators/retryWhen
 * - v9かv10で削除されるので、retry()使えとのこと
 * - いま6なので…
 */
function retryWithDelay() {
  const retryDelay = 3000;
  const retryCount = 3;
  const excludeError = [401, 404];

  return retryWhen((errors) =>
    errors.pipe(
      mergeMap((err, i) => {
        // カウント超過か特定のstatusCodeなら除外（エラーを投げる)
        // - iはconcurrent = カウント。0からスタート
        const hasExcludeCode = excludeError.some(
          (code) => code == (err.status || '')
        );
        const doRetry = hasExcludeCode ? false : i < retryCount;

        return doRetry ? of(err) : throwError(err);
      }),
      tap((err) => console.log(`err:${err.url}, retrying...`)),
      delay(retryDelay),
      concat(throwError('retried too much...'))
    )
  );
}
